import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, AlertCircle, Globe } from 'lucide-react';
import { translations } from '../i18n/translations';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayLang, setDisplayLang] = useState('fr');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const t = translations[displayLang] || translations.fr;

  const formatError = (detail) => {
    if (!detail) return t.common.error;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
    }
    if (detail?.msg) return detail.msg;
    return String(detail);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const userData = await login(email, password);
      if (userData.onboarded === false) {
        navigate('/onboarding');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      data-testid="login-page"
      className="min-h-screen bg-atlasBg flex items-center justify-center p-4 relative overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1771448234067-5de85bece13b?w=1920)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-atlasBg via-atlasBg/90 to-atlasBg/70" />
      </div>

      {/* Glow effect */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-atlasCyan/10 rounded-full blur-3xl" />

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="atlas-brand text-5xl text-white mb-2 tracking-[0.2em]">ATLAS</h1>
          <p className="text-atlasTextSecondary">{(translations[displayLang] || translations.fr).splash.tagline}</p>
        </div>

        {/* Login Card */}
        <div className="glass-card p-8 glow-cyan">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold text-white">{t.auth.welcomeBack}</h2>
            <Select value={displayLang} onValueChange={setDisplayLang}>
              <SelectTrigger className="w-auto border-atlasBorder bg-transparent">
                <Globe className="w-4 h-4 mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fr">🇫🇷 FR</SelectItem>
                <SelectItem value="en">🇬🇧 EN</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-atlasRed/10 border border-atlasRed/30 rounded-lg flex items-center gap-2 text-atlasRed">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-atlasTextSecondary">{t.auth.email}</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="input-atlas mt-1"
                required
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-atlasTextSecondary">{t.auth.password}</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="input-atlas mt-1"
                required
              />
            </div>

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={isLoading}
              className="w-full btn-primary h-12 text-base"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t.common.loading}
                </>
              ) : (
                t.auth.signIn
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <span className="text-atlasTextSecondary">{t.auth.noAccount} </span>
            <Link to="/register" className="text-atlasCyan hover:underline" data-testid="register-link">
              {t.auth.createOne}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
